import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Receipt } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { DataTable } from '../../components/data/DataTable';
import { EmptyState } from '../../components/data/EmptyState';
import { PaymentStatusBadge } from '../../components/payments/PaymentStatusBadge';
import { listPayments, paymentsKey, type PaymentListRow } from '../../lib/queries/payments';

function PaymentsCustomerPage() {
  const [page, setPage] = useState(1);
  const query = { page, limit: 25 };
  const payments = useQuery({
    queryKey: paymentsKey(query),
    queryFn: () => listPayments(query),
  });

  const columns = useMemo<ColumnDef<PaymentListRow, unknown>[]>(
    () => [
      { accessorKey: 'invoiceNo', header: 'Fatura' },
      {
        accessorKey: 'amount',
        header: 'Tutar',
        cell: ({ row }) => `${row.original.amount} ${row.original.currency}`,
      },
      {
        accessorKey: 'status',
        header: 'Durum',
        cell: ({ row }) => <PaymentStatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'dueDate',
        header: 'Vade',
        cell: ({ row }) => format(new Date(row.original.dueDate), 'd MMM yyyy', { locale: tr }),
      },
      {
        accessorKey: 'paidAt',
        header: 'Ödendi',
        cell: ({ row }) =>
          row.original.paidAt
            ? format(new Date(row.original.paidAt), 'd MMM yyyy', { locale: tr })
            : '—',
      },
      {
        accessorKey: 'description',
        header: 'Açıklama',
        cell: ({ row }) => row.original.description ?? '—',
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Ödemelerim" description="Ödeme geçmişiniz ve durumları" />
      <div className="p-6">
        <DataTable
          columns={columns}
          data={payments.data?.data}
          loading={payments.isLoading}
          getRowId={(row) => row.id}
          empty={
            <EmptyState
              icon={Receipt}
              title="Henüz ödemeniz bulunmuyor"
              description="Yeni faturalar burada listelenecek."
            />
          }
          pagination={
            payments.data
              ? {
                  page: payments.data.page,
                  limit: payments.data.limit,
                  total: payments.data.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>
    </>
  );
}

export const Route = createFileRoute('/_app/payments')({ component: PaymentsCustomerPage });
